/**
 * API keys settings section — create and manage API keys.
 * Hidden when the api_keys feature flag is disabled.
 */

import { useCallback } from 'react';

import { useAPIKeys } from '@/hooks/profile/useAPIKeys';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import {
  useInvalidateUserProfile,
  useUserProfileData,
} from '@/hooks/useUserProfileData';
import { useViewHealthMarker } from '@/lib/view-health';

import { APIKeysSection as APIKeysSectionInner } from '../../profile/components/APIKeysSection';

export function APIKeysSection() {
  const hasApiKeysAccess = useFeatureFlag('api_keys');
  const { data: profileData } = useUserProfileData();
  const invalidateUserProfile = useInvalidateUserProfile();
  useViewHealthMarker('settings-section-ready', {
    ready: hasApiKeysAccess && !!profileData,
  });

  const handleKeysChange = useCallback(async () => {
    await invalidateUserProfile();
  }, [invalidateUserProfile]);

  const {
    newApiKey,
    showNewKeyModal,
    creating,
    createAPIKey,
    revokeAPIKey,
    clearNewKey,
  } = useAPIKeys({ onKeysChange: handleKeysChange });

  if (!hasApiKeysAccess) {
    return null;
  }

  return (
    <div
      className='space-y-8'
      data-component='settings-section'
      data-instance='api-keys'
      data-testid='api-keys-section'
    >
      <div>
        <h2 className='text-lg font-semibold text-fg-primary'>API Keys</h2>
        <p className='text-sm text-fg-tertiary mt-0.5'>
          Create and manage API keys for programmatic access.
        </p>
      </div>
      <div>
        <APIKeysSectionInner
          apiKeys={profileData?.api_keys ?? []}
          newApiKey={newApiKey}
          showNewKeyModal={showNewKeyModal}
          creating={creating}
          onCreateKey={createAPIKey}
          onRevokeKey={revokeAPIKey}
          onCloseNewKeyModal={clearNewKey}
        />
      </div>
    </div>
  );
}
